import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";
import {
  PRODUCT_STATUS,
  formatProductPrice,
  getPrimaryPurchaseLink,
  getProductBySlug,
  getProductUi,
  localize,
} from "../../data/products";
import { ConvocoreCallout, InstallationCallout } from "./ProductCallouts";
import "./Products.css";

function DetailList({ title, items, ordered = false }) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <section className="product-detail-panel">
      <h2>{title}</h2>
      <Tag className={ordered ? "product-detail-steps" : "product-detail-list"}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Tag>
    </section>
  );
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const ui = getProductUi(lang);
  const product = getProductBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    document.title = product
      ? `${localize(product.name, lang)} | FIKAMI LLC`
      : `${ui.notFoundTitle} | FIKAMI LLC`;
  }, [lang, product, ui.notFoundTitle]);

  if (!product) {
    return (
      <main className="digital-products-page product-not-found">
        <div className="digital-products-container">
          <span className="digital-products-eyebrow">FIKAMI LLC</span>
          <h1>{ui.notFoundTitle}</h1>
          <p>{ui.notFoundText}</p>
          <Link className="product-button product-button--primary" to="/productos">
            {ui.backCatalog}
          </Link>
        </div>
      </main>
    );
  }

  const purchaseUrl = getPrimaryPurchaseLink(product);
  const demoUrl = product.demoUrl || "";
  const isComingSoon = product.status === PRODUCT_STATUS.COMING_SOON;
  const price = formatProductPrice(product);

  return (
    <main className="digital-products-page product-detail-page">
      <section className="product-detail-hero">
        <div className="digital-products-container product-detail-hero__grid">
          <div className="product-detail-hero__content">
            <Link className="product-back-link" to="/productos">
              ← {ui.backCatalog}
            </Link>
            <div className="product-detail-badges">
              <span>{ui.filters[product.category] || product.category}</span>
              <span>{ui.languageLabel}: {product.language.map((item) => item.toUpperCase()).join(" · ")}</span>
            </div>
            <h1>{localize(product.name, lang)}</h1>
            <p className="product-detail-subtitle">{localize(product.subtitle, lang)}</p>
            <p className="product-detail-description">{localize(product.longDescription, lang)}</p>

            <div className="product-detail-purchase">
              <div>
                <small>{isComingSoon ? "" : localize(product.priceLabel, lang) || ui.from}</small>
                <strong>{isComingSoon ? ui.comingSoon : price}</strong>
              </div>
              {!isComingSoon && demoUrl && (
                <a
                  className="product-button product-button--secondary"
                  href={demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {ui.viewDemo}
                </a>
              )}
              {!isComingSoon && purchaseUrl && (
                <a
                  className="product-button product-button--primary"
                  href={purchaseUrl}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                >
                  {ui.purchaseOn}
                </a>
              )}
            </div>
          </div>

          <div className="product-detail-hero__image">
            <img
              src={product.images.hero}
              alt={localize(product.images.alt, lang)}
            />
          </div>
        </div>
      </section>

      <section className="product-detail-content">
        <div className="digital-products-container">
          <div className="product-detail-layout">
            <DetailList title={ui.included} items={localize(product.features, lang)} />
            <DetailList title={ui.forWhom} items={localize(product.audience, lang)} />
            <DetailList title={ui.howItWorks} items={localize(product.howItWorks, lang)} ordered />
            <DetailList title={ui.requirements} items={localize(product.requirements, lang)} />
            <DetailList title={ui.externalCosts} items={localize(product.externalCosts, lang)} />
            <DetailList title={ui.notIncluded} items={localize(product.notIncluded, lang)} />
          </div>

          {product.installationAvailable && (
            <InstallationCallout ui={ui} installationUrl={product.installationUrl} />
          )}
          <ConvocoreCallout ui={ui} />
        </div>
      </section>
    </main>
  );
}
